<template>
  <div
    ref="pickerOpener"
    class="emoji-picker-wrapper"
  >
    <link
      href="https://css.gg/css"
      rel="stylesheet"
    >
    <v-textarea
      ref="messageContainer"
      v-model="message"
      style="white-space: pre-line;"
      outlined
      auto-grow
      class="mx-3"
      :maxlength="300"
      :rules="[validators.maxLenMessage, validators.minLenMessage, validators.required]"
      rounded
      height="260px"
      dir="auto"
      @input="handleInput"
    >
      <template #append>
        <v-btn
          icon
          @click="openPicker"
        >
          <v-avatar
            size="35"
            class="v-avatar-light-bg primary--text"
          >
            <v-icon
              color="primary"
              size="23"
            >
              {{ icons.mdiEmoticonHappyOutline }}
            </v-icon>
          </v-avatar>
        </v-btn>
      </template>
    </v-textarea>
    <div class="character-count">
      {{ adjustedLength }}/300
    </div>
  </div>
</template>

<script>
import { maxLenMessage, minLenMessage, required } from '@core/utils/validation'
import { mdiEmoticonHappyOutline } from '@mdi/js'

import { createPopup } from '@picmo/popup-picker'

export default {
  name: 'EmojiPicker',
  props: {
    // eslint-disable-next-line vue/require-default-prop
    messageDefault: {
      type: String,
      default: '',
    },
    lang: {
      type: String,
      default: '',
    },
  },
  emits: ['changed'],
  data() {
    return {
      message: this.messageDefault,
      emojiPicker: null,
      adjustedLength: 0,

      icons: {
        mdiEmoticonHappyOutline,
      },

      validators: {
        maxLenMessage,
        minLenMessage,
        required,
      },
    }
  },
  watch: {
    message(currentVal) {
      this.adjustedLength = this.calculateAdjustedLength(currentVal)

      this.emojiCounter = this.countEmojis(currentVal)

      this.$emit('changed', currentVal)
    },
  },
  mounted() {
    this.emojiPicker = createPopup(
      {
        visibleRows: 4,
        emojiSize: '1.5rem',
        emojisPerRow: 7,
      },
      {
        referenceElement: this.$refs.pickerOpener,
        triggerElement: this.$refs.pickerOpener,
        position: 'auto',
      },
    )

    // Calculate the initial adjusted length based on the default message
    this.adjustedLength = this.calculateAdjustedLength(this.message)

    this.emojiPicker.addEventListener('emoji:select', event => {
      const input = this.$refs.messageContainer.$el.querySelector('textarea')
      const start = input.selectionStart
      const end = input.selectionEnd

      // Check if there is sufficient space to add an emoji, which counts as two characters
      if (this.adjustedLength <= 300 - 2) {
        // Insert the emoji at the current cursor position
        const textBefore = this.message.substring(0, start)
        const textAfter = this.message.substring(end)
        const newText = textBefore + event.emoji + textAfter

        this.message = newText
      }

      // Update the adjusted length after inserting an emoji
      this.adjustedLength = this.calculateAdjustedLength(this.message)
    })
  },
  methods: {
    openPicker() {
      if (this.emojiPicker) {
        this.emojiPicker.toggle()
      }
    },
    countEmojis(text) {
      const regex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})\p{Emoji_Modifier_Base}?/gu

      return (text.match(regex) || []).length
    },
    calculateAdjustedLength(text) {
      const regex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})\p{Emoji_Modifier_Base}?/gu
      const plainTextLength = text.replace(regex, '').length
      const emojiCount = (text.match(regex) || []).length

      return plainTextLength + emojiCount * 2
    },
    handleInput() {
      if (this.calculateAdjustedLength(this.message) > 300) {
        let cuttingPoint = this.message.length - 1

        // Keep cutting back until the length is within limit
        while (this.calculateAdjustedLength(this.message.substring(0, cuttingPoint)) > 300 && cuttingPoint > 0) {
          cuttingPoint--
        }
        this.message = this.message.substring(0, cuttingPoint)
      }
    },
  },
}
</script>
<style>
.popupContainer {
  z-index: 999;
}
.picmo-picker .emojiArea {
  font-size: 0.85rem;
}
.picmo__picker .picmo__emojiContainer {
  padding: 0 1.5em !important;
  justify-content: flex-start !important;
}
</style>
<style scoped>
::v-deep.emojiCard {
  border: 1px solid var(--v-primary-base);
}
::v-deep.emoji-picker-wrapper .v-input__append-inner {
  margin-top: 5px;
}
::v-deep.v-text-field--rounded > .v-input__control > .v-input__slot {
  padding-right: 10px;
  padding-left: 10px;
}
.character-count {
  font-size: 0.75rem;
  color: #757575;
  text-align: left;
  margin-left: 20px;
  margin-top: -20px;
}
</style>
